import eol from 'eol'
import _ from 'lodash'
import semver from 'semver'

import {
  addChartPoint,
  addIssues,
  setMaxTemp,
  setMaxThreads,
  setSolsticeVersion,
  setStorage,
} from './actions'
import { CPUkeys } from './CPU'
import { badKeywords } from './keywords'
import { getStore } from './store'
import {
  averagedTempKeys,
  // @ts-ignore
  individualTempKeysGen2i,
  // @ts-ignore
  individualTempKeysGen3
} from './Temperature'
import { threadKeys } from './Threads'
import {
  ChartType,
  IChartPoint,
  IFile,
  IIssue,
  IKeyword,
  IMaxThreads,
  IState,
  IStorage,
  ITemperature,
  KeywordType,
 } from './types'

const checkForSolsticeVersion = (line: string): void => {
  // 'Solstice Version - 3.3.2 Build# 10454'
  // if multiple build and versions are encountered, latest is considered
  const versionString = 'Solstice Version - '
  const buildNumberString = 'Build# '
  if (_.includes(line, versionString)) {
    const thisVersion = line.split(versionString)[1].split(' ')[0]
    const thisBuildNumber = line.split(buildNumberString)[1].split(' ')[0]
    const store = getStore()
    const {
      solsticeVersion,
      buildNumber,
    } = (store.getState() as IState)

    const intBuildNumber = parseInt(buildNumber, 10) || 0
    const intThisBuildNumber = parseInt(thisBuildNumber, 10) || 0

    const newerBuild: boolean = buildNumber === 'Unknown' || (intThisBuildNumber > intBuildNumber)
    const newerVersion: boolean = solsticeVersion === 'Unknown' || semver.gt(thisVersion, solsticeVersion)

    if (newerVersion || newerBuild) {
      store.dispatch(setSolsticeVersion(thisVersion, thisBuildNumber))
    }

  }
}

const checkStorage = (line: string, storage: IStorage): IStorage => {

  // ServerDisplay: Storage /data: total=9951MB, available=8397MB
  const storageTotal: string = 'data: total='
  const storageAvailable: string = ', available='
  let result: IStorage = storage
  if (_.includes(line, storageTotal)) {
    const total: number = parseInt(line.split(storageTotal)[1].split('MB')[0], 10)
    const available: number = parseInt(line.split(storageAvailable)[1].split('MB')[0], 10)
    const percent: string = Math.round(available * 100 / total).toString() + '%'
    result = {
      available,
      percent,
      total,
    }
  }
  return result
}

const extractTimes = (line: string): [number, string] => {
    // don't use moment, it's too slow
    // timestamp format at beginning of lines
    // 09-17 15:57:28.946

    const dateString: string = `2000-${line.substr(0, 18)}` // append a dummy year, we never use it
    const timestamp: number = Date.parse(dateString)
    const months = {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mar',
      '04': 'Apr',
      '05': 'May',
      '06': 'Jun',
      '07': 'Jul',
      '08': 'Aug',
      '09': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    }
    const name: string = `${dateString.substr(8, 2)}-${months[dateString.substr(5, 2)]} ${dateString.substr(11, 5)}`
    return [timestamp, name]
}

const checkTemperaturesGen2i = (line: string, lineNumber: number, fileId: number, chartPoints: IChartPoint[], current: ITemperature | undefined): ITemperature | undefined => {
  // pre-gen3:
  // note the double space between 5 and 6 ...
  // 10-23 06:48:09.771  1372  1762 D SOLSTICE_LOGGER: * Information * TimerCallback 1-Minute thermal zone report: 0=58 1=56 2=57 3=57 4=59 5=59  6=58 7=60 8=58 9=60 10=59 Solstice/Server/Src/SolsticeServer.cpp 195

  const thermal: string = 'thermal zone report: '
  if (_.includes(line, thermal)) {
    const temps: number[] = _
      .chain(line)
      .split(thermal)
      .last()
      .split(' ')
      .filter(element => !!element) // deals with the double space
      .take(11)
      .map(temp => parseInt(temp.split('=')[1], 10))
      .value()!

    const avg = (temperatures: number[], start: number, end: number) => _
      .chain(temperatures)
      .slice(start, end)
      .mean()
      .round()
      .value()

    const averagedTemps: number[] = [
      avg(temps, 5, 8), // cpu
      avg(temps, 9, 10), // gpu
      avg(temps, 0, 10), // all
    ]

    const [timestamp, name] = extractTimes(line)

    const chartType = 'TEMPERATURE' as ChartType

    const chartDataPoint: IChartPoint = Object.assign(
      {},
      // _.zipObject(individualTempKeysGen2i, temps), // use all the sensors
      _.zipObject(averagedTempKeys, averagedTemps), // average out over cpu, gpu, and all
      {
        chartType,
        fileId,
        lineNumber,
        name,
        timestamp,
      },
    )

    const max: number = Math.max(...temps)

    chartPoints.push(chartDataPoint)

    const result: ITemperature | undefined = (max > (current || { max: 0 }).max)
      ?
        {
          fileId,
          lineNumber,
          max,
        }
      : current

    return result
  }
  return current
}

const checkTemperaturesGen3 = (line: string, lineNumber: number, fileId: number, chartPoints: IChartPoint[], current: ITemperature | undefined, temps: number[]): [number[], ITemperature | undefined] => {
  // gen-3:
  // note that it's on multiple lines
  /*
    2020-12-08 11:57:31.549 18851-19027/com.mersive.solstice.server D/SOLSTICE_LOGGER: * Information * TimerCallback 1-Minute thermal zone report (centigrade): 
     0=63.4  1=66.6  2=63.4  3=67.2  4=67.5  5=67.9  6=67.5  7=66.9  8=68.2  9=66.9
    10=67.2 11=66.9 12=67.2 13=64.7 14=61.1 15=62.7 16=63.4 17=65.3 18=62.7 19=60.2 20=61.8
     Solstice/Server/Src/SolsticeServer.cpp 161
  */

  const THERMALS_1: RegExp = /0=(\d+.?\d?)\s+1=(\d+.?\d?)\s+2=(\d+.?\d?)\s+3=(\d+.?\d?)\s+4=(\d+.?\d?)\s+5=(\d+.?\d?)\s+6=(\d+.?\d?)\s+7=(\d+.?\d?)\s+8=(\d+.?\d?)\s+9=(\d+.?\d?)/;
  const THERMALS_2: RegExp = /10=(\d+.?\d?)\s+11=(\d+.?\d?)\s+12=(\d+.?\d?)\s+13=(\d+.?\d?)\s+14=(\d+.?\d?)\s+15=(\d+.?\d?)\s+16=(\d+.?\d?)\s+17=(\d+.?\d?)\s+18=(\d+.?\d?)\s+19=(\d+.?\d?)\s+20=(\d+.?\d?)/;

  const avg = (temperatures: number[], start: number, end: number) => _
    .chain(temperatures)
    .slice(start, end)
    .mean()
    .round()
    .value()
  ;

  if (THERMALS_1.test(line)) {
    const matches = THERMALS_1.exec(line);
    // @ts-ignore
    matches.shift(); // remove the original string
    const temps: number[] = _
      .chain(matches)
      .map(temp => parseInt(temp, 10))
      .value()!

    return [temps, current];
  }
  if (THERMALS_2.test(line)) {
    const matches = THERMALS_2.exec(line);

    // @ts-ignore
    matches.shift(); // remove the original string
    const temps: number[] = _
      .chain(matches)
      .map(temp => parseInt(temp, 10))
      .value()!

    const allTemps = temps.concat(temps);

    // now do analysis
    const averagedTemps: number[] = [
      avg(allTemps, 3, 12), // cpu
      avg(allTemps, 14, 15), // gpu
      avg(allTemps, 0, 20), // all
    ]

    const [timestamp, name] = extractTimes(line)

    const chartType = 'TEMPERATURE' as ChartType

    const chartDataPoint: IChartPoint = Object.assign(
      {},
      // individualTempKeysGen3
      _.zipObject(averagedTempKeys, averagedTemps), // average out over cpu, gpu, and all
      {
        chartType,
        fileId: fileId,
        lineNumber,
        name,
        timestamp,
      },
    )

    chartPoints.push(chartDataPoint)

    const max: number = Math.max(...temps)

    const result: ITemperature | undefined = (max > (current || { max: 0 }).max)
      ?
        {
          fileId,
          lineNumber,
          max,
        }
      : current

    return [[], result];
  }
  
  return [[], current];
}

const checkMaxThreadCount = (line: string, lineNumber: number, fileId: number, maxThreads: IMaxThreads, chartPoints: IChartPoint[]): IMaxThreads => {
  // ThreadFunc WATCHDOG: Number of threads = 96 jni/..
  const threads = 'Number of threads = '
  // /Number of threads = (\d+)/
  let result: IMaxThreads = maxThreads
  if (_.includes(line, threads)) {
    const thisMaxThreads: number = _
      .chain(line)
      .split(threads)
      .last()
      .split(' ')
      .take(1)
      .parseInt()
      .value()

    const [timestamp, name] = extractTimes(line)

    const chartType = 'THREADS' as ChartType

    const chartDataPoint: IChartPoint = Object.assign( _.zipObject(threadKeys, [thisMaxThreads]), { // average out over cpu, gpu, and all
        chartType,
        fileId,
        lineNumber,
        name,
        timestamp,
      },
    )

    chartPoints.push(chartDataPoint)

    result = thisMaxThreads > maxThreads.max
      ?
        {
          fileId,
          lineNumber,
          max: thisMaxThreads,
        }
      : maxThreads
  }
  return result
}

const checkBadFirewallRules = (line: string, lineNumber: number, fileId: number): IIssue | undefined => {
  // this line is ok:
  // -A FORWARD -s 192.168.43.0/24 -o eth0 -j REJECT --reject-with icmp-port-unreachable
  // this line is NOT ok:
  // -A FORWARD eth0 -j REJECT --reject-with icmp-port-unreachable
  const reject: string = ' REJECT '
  const specifier: string = ' -s '
  let result: IIssue | undefined;

  if (_.includes(line, reject) && !_.includes(line, specifier)) {
    result = {
      fileId,
      id: lineNumber,
      info: 'Out of order firewall rules will cause a pod to reject all traffic, and become unpingable.',
      lineNumber,
      name: 'Bad firewall rule',
      type: 'BAD_FIREWALL_RULE',
    }
  }
  return result
}

const checkBlockedThreads = (line: string, lineNumber: number, fileId: number): IIssue | undefined => {

  const WATCHDOG: string = 'WATCHDOG'
  const CONNECTION_MANAGER: string = 'ConnectionManager) blocked for '
  const NOTIFICATION_THREAD: string = 'NotificationThread) blocked for '
  let result
  if (_.includes(line, CONNECTION_MANAGER) && _.includes(line, WATCHDOG)) {
    result = {
      fileId,
      id: lineNumber,
      info: 'We\'ve occasionally seen mersive::NetworkTransportImpl::ConnectionManager become blocked for a long time before crashing.',
      lineNumber,
      name: 'ConnectionManager',
      type: 'BLOCKED_THREAD_CONNECTIONMANAGER' as KeywordType,
    }
  } else if (_.includes(line, NOTIFICATION_THREAD) && _.includes(line, WATCHDOG)) {
    result = {
      fileId,
      id: lineNumber,
      info: 'If the mersive::NetworkTransportImpl::NotificationThread becomes blocked, it will often never unblock, and the pod may eventually crash.',
      lineNumber,
      name: 'NotificationThread',
      type: 'BLOCKED_THREAD_NOTIFICATIONTHREAD' as KeywordType,
    }
  }
  return result
}

const checkCPU = (line: string, lineNumber: number, fileId: number, chartPoints: IChartPoint[]): void => {
// 09-13 17:18:36.895  1326  1411 D SOLSTICE_LOGGER: User 31%, System 23%, IOW 0%, IRQ 2%
  const userCPUsignature: string = 'SOLSTICE_LOGGER: User '
  const systemCPUsignature: string = '%, System '
  if (_.includes(line, userCPUsignature) && _.includes(line, '%')) {
    const getPercent = (lyne: string, splitter: string): number => _
      .chain(lyne)
      .split(splitter)
      .tail()
      .split('%')
      .map(s => parseInt(s, 10))
      .head()
      .value()!
    const userCPUpercent: number = getPercent(line, userCPUsignature)
    const systemCPUpercent: number = getPercent(line, systemCPUsignature)

    const [timestamp, name] = extractTimes(line)

    const chartType = 'CPU' as ChartType

    const chartDataPoint: IChartPoint = Object.assign(_.zipObject(CPUkeys, [userCPUpercent, systemCPUpercent]), { // average out over cpu, gpu, and all
        chartType,
        fileId,
        lineNumber,
        name,
        timestamp,
      },
    )

    chartPoints.push(chartDataPoint)
  }
}

const maybeDispatch = (action: any, data: any): void => {
  if (!!data) {
    getStore().dispatch(action(data))
  }
}

const maybeConcat = (issues: IIssue[], data: any) => {
  if (!!data) {
    issues.push(data)
  }
}

const checkMemory = (line: string, lineNumber: number, fileId: number, chartPoints: IChartPoint[]): void => {
  // MEMORY MONITOR (KB): com.mersive.solstice.server=1234,com.mersive.solstice.server:monitorservice=9868,com.splashtop.m360.mersive=14029,com.mersive.keystoreinitializer=3797,time=1538766558448,total=2997172,free=2101098,used=527888

  const signature: string = 'MEMORY MONITOR (KB): '
  if (!line.includes(signature)) {
    return
  }

  const data = line.split(signature)[1].split(' ')[0].split(',')
  const filteredData = data.filter(el => !el.includes('time'))
  const keys = filteredData.map(element => element.split('=')[0])
  const values = filteredData.map(element => parseInt(element.split('=')[1], 10))
  const result = _.zipObject(keys, values)

  const chartType = 'MEMORY' as ChartType

  const [timestamp, name] = extractTimes(line)

  const chartDataPoint: IChartPoint = Object.assign(result, {
      chartType,
      fileId,
      lineNumber,
      name,
      timestamp,
    },
  )

  chartPoints.push(chartDataPoint)

}

const analyze = (file: IFile, keywords: IKeyword[] = badKeywords): void => {

  const lines: string[] = eol.split(file.content)

  // batch up all the issues, and dispatch one action per file
  // dispatching action per issues causes too many rerenders, and perf shits the bed
  const state: IState = getStore().getState()
  const issues: IIssue[] = []
  const chartPoints: IChartPoint[] = []
  let maxTemp: ITemperature | undefined = state.temperature
  let storage: IStorage = state.storage
  let maxThreads: IMaxThreads = state.maxThreads

  let gen3temperatures: number[] = []
  lines.forEach((line: string, lineNumber: number) => {

    const lowerCaseLine: string = line.toLowerCase()

    for (const keyword of keywords) {
      if (!lowerCaseLine.includes(keyword.signature.toLowerCase())) {
        continue // no issues on this line
      }

      // pull the first 14 chars of the line, it might be a timestamp
      const maybeTimestamp = line.substring(0, 14)
      // if it includes '---' it's probably like ----beginning of main... so just use nothing
      const secondary = maybeTimestamp.includes('---') ? '' : maybeTimestamp

      issues.push({
        fileId: file.id,
        id: lineNumber,
        info: keyword.info,
        lineNumber,
        name: keyword.name,
        secondary,
        type: keyword.alias,
      })
      break
    }

    checkMemory(line, lineNumber, file.id, chartPoints)
    checkForSolsticeVersion(line)
    checkCPU(line, lineNumber, file.id, chartPoints)

    // The Gen2i and Gen3 have different formats for the temperatures.
    // The Gen3 format is split across two lines, which makes our line-by-line parsing
    // difficult, and requires us to now be stateful to combine the different lines together.
    maxTemp = checkTemperaturesGen2i(line, lineNumber, file.id, chartPoints, maxTemp);
    [gen3temperatures, maxTemp] = checkTemperaturesGen3(line, lineNumber, file.id, chartPoints, maxTemp, gen3temperatures);

    storage = checkStorage(line, storage)
    maxThreads = checkMaxThreadCount(line, lineNumber, file.id, maxThreads, chartPoints)
    maybeConcat(issues, checkBadFirewallRules(line, lineNumber, file.id))
    maybeConcat(issues, checkBlockedThreads(line, lineNumber, file.id))
  })

  maybeDispatch(addIssues, issues)
  maybeDispatch(setMaxTemp, maxTemp)
  maybeDispatch(setStorage, storage)
  maybeDispatch(setMaxThreads, maxThreads)
  maybeDispatch(addChartPoint, chartPoints)

}

export default analyze


// WEBPACK FOOTER //
// ./src/analyze.ts